/**
 * **节点编辑回调 + 画布事件分发** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 模板 / 图引擎抛上来的那一堆回调，按对象分三类：
 *
 *   **节点内部编辑** —— `onBoxResize`（框 / 注释改尺寸）、`onRenameNode`、
 *   `onRepeaterAdd` / `onRenameRepeater` / `onRowToggle`（变参行）、`onNoteUpdate`
 *   **画布 / 连线事件** —— `onEdgeDoubleClick` + `insertRerouteOnEdge`、
 *   `onPaneMenu` / `onSelectionMenu` / `onEdgeMenu`（四个右键入口）、
 *   `onGraphDoubleClick`
 *   **批量操作** —— `deleteSelectedNodes` / `deleteNodesById` / `selectAll`
 *
 * ⚠️ Vue Flow 有**四个**右键入口（node / pane / selection / edge），每一个都只是
 * 把事件转发出来、自己不 preventDefault —— 漏掉任何一个，那条路径上就会同时
 * 弹出浏览器默认菜单。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `frameBoxes` / `childrenOfFrame` / `projectOntoEdge` / `edgeMuted` 这些
 * 归属别处（`useGrouping` / `useMenus` / 宿主）的助手由 `ctx` 注入。
 */
import { nextTick } from 'vue'
import type { PointerLikeEvent } from './useTouchMenu'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import { planFrameDeletion } from '@/graph/frame'
import { FLOW_DOM } from '@/graph/graphDom'
import type { FrameDeleteMode } from '@/graph/frame'
import { splitEdgeForReroute } from '@/graph/reroute'
import { findSocketIn } from '@/graph/linkRules'
import { reconnectAroundReroutes } from '@/graph/reroute'
import type { Point } from '@/graph/cutGeometry'
import type { FlowStoreApi } from '@/graph/flowStore'
import type { GraphNodeModel, NodeRow } from '@/types/node'

export interface NodeEditingCtx {
  nodes: { value: Node[] }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  graphRef: { value: HTMLElement | null }
  activeNodeId: { value: string | null }
  selectedIds: { value: string[] }
  menu: { value: { kind: string; x: number; y: number } | null }
  contextEdgeId: { value: string | null }
  edgeViews: () => EdgeViewLike[]
  modelOf: (id: string) => GraphNodeModel | undefined
  /** 按 id 找节点（useGrouping 注入） */
  looseNode: (id: string) => { id: string; position: { x: number; y: number } } | undefined
  frameBoxes: () => { id: string }[]
  childrenOfFrame: (frameId: string) => string[]
  isStructurallyValid: (
    s: string,
    sh: string | null | undefined,
    t: string,
    th: string | null | undefined,
  ) => boolean
  edgeMuted: (edgeId: string) => boolean
  projectOntoEdge: (edgeId: string, ev: MouseEvent | TouchEvent) => Point | null
  /** 屏幕坐标 → 画布坐标（来自 useAddNode） */
  toFlowPoint: (p: { x: number; y: number }) => { x: number; y: number }
  /** 鼠标 / 触摸事件坐标（来自 useMenus） */
  eventPoint: (ev: MouseEvent | TouchEvent) => { x: number; y: number }
  /** 选中 + 聚焦（来自 useAddNode） */
  focusNode: (id: string) => void
  /** 受保护的节点（组接口节点等，不参与删除）—— 来自 useSubgraphLevels 的注入 */
  isProtectedNode: (id: string) => boolean
  /**
   * 把**当前层**的接口改动刷到外层（来自 `useSubgraphLevels` 的注入）。
   *
   * ⚠️ 它是那个 composable 里的私有函数，不是 `node/subgraph.ts` 的导出 ——
   * 因为它要用「当前进到哪一层」（`currentOwnerId`）。
   * 接口节点上改名之后要叫它一次（见 `onRenameRepeater`）。
   */
  refreshInterfaceOf: (ownerId: string) => void
  /** 打开「添加节点」菜单（来自 useAddNode） */
  openAddMenu: (point?: { x: number; y: number }) => void
  /* ── 造节点 / 造边的工具（宿主里都是函数声明，注入即可） ── */
  nextNodeId: (type: string) => string
  makeModelOfType: (type: string, id: string) => GraphNodeModel | null
  makeFlowNode: (model: GraphNodeModel, position: { x: number; y: number }) => Node
  makeEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) => Edge
  doUndo: () => void
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string, actions?: NoticeActionInput | NoticeActionInput[]) => void
}

/**
 * 提示条动作 —— 只描述这个文件用得到的形状（与 useNotice 的相容）。
 */
export interface NoticeActionInput {
  label: string
  run: () => void
}

/** 边视图 —— 只描述这个文件用得到的形状 */
export interface EdgeViewLike {
  id?: string
  muted?: boolean
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

export function useNodeEditing(ctx: NodeEditingCtx) {
  const {
    nodes,
    edges,
    flowStore,
    graphRef,
    activeNodeId,
    selectedIds,
    menu,
    contextEdgeId,
    edgeViews,
    modelOf,
    looseNode,
    frameBoxes,
    childrenOfFrame,
    isStructurallyValid,
    edgeMuted,
    projectOntoEdge,
    nextNodeId,
    makeModelOfType,
    makeFlowNode,
    makeEdge,
    doUndo,
    toFlowPoint,
    eventPoint,
    focusNode,
    openAddMenu,
    isProtectedNode,
    refreshInterfaceOf,
    commit,
    showNotice,
  } = ctx

/** 框 / 注释被拖右下角改尺寸 */
function onBoxResize(nodeId: string, width: number, height: number) {
  // 连续拖拽合并成一步历史
  commit('调整尺寸', 500)
  const model = modelOf(nodeId)
  if (!model) return
  model.width = width
  model.height = height
}

/**
 * 就地改名（双击标题）。
 *
 * 空字符串 = 清掉自定义名，回到类型自带的标题。
 */
function onRenameNode(nodeId: string, label: string) {
  const model = modelOf(nodeId)
  if (!model) return
  commit('改名称')
  model.label = label === '' ? undefined : label
}

/**
 * 变参行加一个实例。
 *
 * 「＋」的语义是**先摆一行空着等接线** —— 空行推不出来（没有边），
 * 所以必须显式记在 `row.count` 上。
 */
function onRepeaterAdd(nodeId: string, rowId: string) {
  const row = modelOf(nodeId)?.rows.find((r) => r.id === rowId)
  if (row?.kind !== 'repeater') return
  commit('加变参行')
  const current =
    row.count ?? Math.max(row.min ?? 1, (row.connected ?? []).length + 1)
  row.count = Math.min(row.max ?? Number.POSITIVE_INFINITY, current + 1)
  nextTick(() => flowStore.value?.updateNodeInternals([nodeId]))
}

/**
 * 变参行实例改名。
 *
 * 名字只是**外观** —— 端口身份是 id + 序号，运行端按那个识别。
 * 起过名的实例不会再被「自动跟随上游」覆盖（优先级见 RepeaterRow.names）。
 */
function onRenameRepeater(nodeId: string, rowId: string, index: number, name: string) {
  const model = modelOf(nodeId)
  const row = model?.rows.find((r) => r.id === rowId)
  if (row?.kind !== 'repeater') return
  commit('改变参名')
  const names = [...(row.names ?? [])]
  while (names.length <= index) names.push(undefined)
  names[index] = name === '' ? undefined : name
  row.names = names

  /*
   * ★★ **接口节点上的改名要同步到外层。**
   *
   * ⚠️ 用户报过：「变参行修改名称后，循环外部没有同步变化」——
   * 组也一样（两边镜像都从内层的 `names` 取名字，但那时**根本没取**，
   * 外层一律显示写死的「参数 N」/「结果 N」）。
   *
   * 现在取名字那条路通了（`groupRowsFor` / `loopExternalRows` 收 `names`），
   * 这里负责**改名之后触发一次重算**。而外面那个名字正是接线时
   * 唯一能看到的提示 —— 不同步等于改名没用。
   *
   * ⚠️ 只在外层**已经有对应的那一行**时才改（按 id 找）：
   * 外面路数和里面不一定相等（外面是静态镜像，里面还有空槽），
   * 找不到就说明这一路外面没显示，不必硬造。
   *
  /*
   * ★★ **接口节点上的改名要同步到外层。**
   *
   * ⚠️ 用户报过：「变参行修改名称后，循环外部没有同步变化」—— 组也一样。
   * 根因有两层：① 外层镜像**根本没读内层的 `names`**（一直是写死的
   * 「参数 N」/「结果 N」）；② 改完名**没有触发外层重算**。
   * ① 已在 `groupRowsFor` / `loopExternalRows` 修好（它们现在收 `names`），
   * 这里补 ②。
   *
   * ⚠️ 用现成的 `refreshInterfaceOf(nodeId)`，**不另写一条「定向改标签」的路**：
   * 那种写法要自己反解外层端口 id（`in.{i}` / `out{i}` / `params.{i}` 三套），
   * 猜错就静默改到别的行上。刷新那条路已经会读 `names` 了，走它就够。
   *
   * ⚠️ 它是**逐字符**触发的（`@input` 实时发），所以这条刷新必须便宜：
   * `refreshInterfaceOf` 只重建 `model.rows` 这个数组，不碰画布。
   */
  refreshInterfaceOf(nodeId)
}/**
 * 行上的小开关（循环输出行的「跳过空值」）。
 *
 * 它是**功能数据**（改变求值结果），所以跟着 rows 一起进历史、
 * 也会进 RenderTree —— 跟 `width` 那种设计器数据不是一回事。
 */
function onRowToggle(nodeId: string, rowId: string, value: boolean) {
  commit('切换跳过空值')
  const model = modelOf(nodeId)
  const row = model?.rows.find((r) => r.id === rowId)
  if (row?.kind === 'output') row.skipEmpty = value
}

/** 注释正文。连续输入合并成一步历史，不然每敲一个字都进历史 */
function onNoteUpdate(nodeId: string, text: string) {
  commit('修改注释', 800)
  const model = modelOf(nodeId)
  if (model) model.note = text
}

/**
 * 双击线条 → 在线上拆出一个接通节点。
 *
 * 原来的边 A → B 变成 A → ● 和 ● → B 两段。
 * 两端的 handle 原样保留，所以类型校验结果不变 —— 不需要重新判定。
 */
function onEdgeDoubleClick(params: {
  event: MouseEvent | TouchEvent
  edge: {
    id?: string
    source: string
    sourceHandle?: string | null
    target: string
    targetHandle?: string | null
  }
}) {
  const edge = params.edge
  if (!edge?.id) return

  insertRerouteOnEdge(edge, projectOntoEdge(edge.id, params.event), params.event)
}

/** 在某条边上拆出一个接通节点。双击线条和右键菜单都走这里 */
function insertRerouteOnEdge(
  edge: {
    id?: string
    source: string
    sourceHandle?: string | null
    target: string
    targetHandle?: string | null
  },
  at: Point | null,
  ev?: MouseEvent | TouchEvent,
) {
  if (!edge?.id) return
  const position = at ?? (ev ? toFlowPoint(eventPoint(ev)) : null)
  if (!position) return

  const id = nextNodeId('reroute')
  const model = makeModelOfType('reroute', id)
  if (!model) return

  commit('插入接通节点')

  type Loose = Record<string, unknown>
  const parts = splitEdgeForReroute(edge, id)
  const rest = (edges.value as unknown as { id?: string }[]).filter(
    (e) => e.id !== edge.id,
  )

  nodes.value = [
    ...(nodes.value as unknown as Loose[]),
    makeFlowNode(model, position) as unknown as Loose,
  ] as unknown as Node[]

  edges.value = [
    ...rest,
    makeEdge(parts.upstream.source, parts.upstream.sourceHandle ?? '', parts.upstream.target, parts.upstream.targetHandle ?? ''),
    makeEdge(parts.downstream.source, parts.downstream.sourceHandle ?? '', parts.downstream.target, parts.downstream.targetHandle ?? ''),
  ] as unknown as Edge[]

  nextTick(() => flowStore.value?.updateNodeInternals([edge.source, edge.target, id]))
  /*
   * ★ 选中**并设为活动**。
   * 只选中不够 —— 属性面板跟的是活动节点，而接通节点的名称只能在
   * 属性面板里改（它没有 header 可以双击），于是「标题没法编辑」。
   */
  focusNode(id)
  showNotice(
    `已插入接通节点${model.label ? `「${model.label}」` : ''} —— 名称在右侧属性面板里改`,
  )
}

/**
 * 空白画布右键 / 长按。
 *
 * ⚠️ 参数用 `PointerLikeEvent`（结构类型）而不是 `MouseEvent | TouchEvent`：
 * 触摸端那条路径给的是 `PointerEvent`（它确实满足前者，但
 * 和 `MouseEvent | TouchEvent` 这个联合**结构上不兼容**）。
 * 用结构类型两边都收得下，也不用在桥接层写 `as never` 去抹平差异。
 */
function onPaneMenu(ev: PointerLikeEvent) {
  ev.preventDefault?.()
  contextEdgeId.value = null
  /*
   * ⚠️ 自己取坐标，不转给 `eventPoint(ev)` —— 那个函数收的是
   * `MouseEvent | TouchEvent`，而这里来的是结构类型 `PointerLikeEvent`
   * （两条调用路径：Vue Flow 给的事件、触摸长按给的 PointerEvent）。
   * 两边都有 `clientX/clientY`，直接读就够了。
   */
  menu.value = { kind: 'context', x: ev.clientX, y: ev.clientY }
}

/** 多选时那层选择框自己会拦截右键，走的是这个事件 */
function onSelectionMenu(params: { event: MouseEvent | TouchEvent }) {
  params.event.preventDefault()
  contextEdgeId.value = null
  menu.value = { kind: 'context', ...eventPoint(params.event) }
}

/** 右键连线 */
function onEdgeMenu(params: { event: MouseEvent | TouchEvent; edge: { id?: string } }) {
  params.event.preventDefault()
  contextEdgeId.value = params.edge?.id ?? null
  menu.value = { kind: 'context', ...eventPoint(params.event) }
}

/**
 * 双击**空白画布** → 就地添加节点。
 *
 * ⚠️ Vue Flow **没有** `paneDoubleClick` 这个 emit
 * （有 paneClick / paneContextMenu / paneMouseMove…，就是没有双击），
 * 所以挂在容器上听原生 `dblclick`。
 *
 * ★ 用**允许列表**：只有点在 `.vue-flow__viewport` **之外**才算「空白处」。
 *
 * 节点、连线、端口全都在 viewport 里面，空白画布不在 —— 这个判断是准的。
 *
 * ⚠️ 一开始我判的是 `.vue-flow__pane`，**不对**：
 * `pane` 是那个铺满整个画布的容器（z-index 1），节点也在它的范围里，
 * 于是**双击任何节点都会弹出添加菜单**。排除列表更容易漏
 * （连线、端口、小地图…），所以一定要用「点在 viewport 内」这条正向判断。
 */
function onGraphDoubleClick(ev: MouseEvent) {
  const target = ev.target as HTMLElement | null
  if (!target) return

  // 双击节点不再做任何事 —— 进入子图改走节点上那个明确的按钮
  // （双击和「双击改名」「双击排序」都容易撞车，不如给个确定入口）
  /* ⚠️ 选择器从 `graphDom.ts` 取（引擎的 DOM 契约） */
  if (target.closest(`.${FLOW_DOM.node}`)) return

  if (target.closest('.np-menu')) return
  if (target.closest('.np-log')) return

  /*
   * ★ 只有空白处才开添加菜单。
   *
   * 判据是「点在 `.vue-flow__viewport` **之外**」—— 节点、连线、端口
   * 全在 viewport 里面，空白画布不在。
   *
   * ⚠️ 别改成判 `.vue-flow__pane`：那是铺满整个画布的容器，节点也在
   * 它的范围里，于是**双击任何节点都会弹菜单**（这正是之前的 bug）。
   * 也别忘了这一步 —— 少了它，双击连线会同时冒出接通节点和添加菜单。
   */
  if (target.closest(`.${FLOW_DOM.viewport}`)) return
  openAddMenu(eventPoint(ev))
}

/**
 * 删除选中的节点。
 *
 * 连带删掉挂在它们身上的边 —— 不删的话那些边会指向不存在的节点，
 * 图引擎会直接报错（而且撤销回来也对不上）。
 */
/**
 * 删除选中的节点。
 *
 * `mode` 只在选中里有**框**时才有意义：
 *   'frame-only'    只删框，成员留在原地
 *   'with-children' 连成员一起删
 *
 * 快捷键：`Delete` 走默认（只删框，并在提示条里给「连子节点一起删」的选择）、
 * `Shift+Delete` 全删、`Alt+Delete` 只删框。
 */
function deleteSelectedNodes(mode: FrameDeleteMode = 'frame-only') {
  // 组 / 循环内部的接口节点不给删 —— 删了整层就废了
  const ids = selectedIds.value.filter((id) => !isProtectedNode(id))
  if (ids.length === 0) {
    if (selectedIds.value.length) showNotice('接口节点不能删除')
    return
  }

  const plan = planFrameDeletion(
    ids,
    mode,
    (id) => modelOf(id)?.render === 'frame',
    childrenOfFrame,
  )
  if (plan.deleted.length === 0) return

  commit('删除节点')
  const drop = new Set(plan.deleted)

  // 只删框时，成员要「脱离」—— 不清 frameId 会留下指向已删框的悬空引用
  for (const id of plan.orphaned) {
    const model = modelOf(id)
    if (model) model.frameId = undefined
  }
  type Loose = Record<string, unknown>

  /*
   * ★ 被删掉的**接通节点**要把上下游接回去。
   *
   * 只删节点不接线的话，会留下指向已删节点的边 —— 图引擎直接报错。
   * 而且 `reconnectAroundReroutes` 能穿过**一串**一起被删的接通节点，
   * 所以这里把全部要删的接通节点一次性交给它。
   */
  const rerouteIds = new Set(
    plan.deleted.filter((id) => modelOf(id)?.render === 'dot'),
  )
  const bridged = reconnectAroundReroutes(
    edgeViews() as unknown as { source: string; sourceHandle?: string | null; target: string; targetHandle?: string | null; muted?: boolean }[],
    rerouteIds,
  )

  nodes.value = (nodes.value as unknown as Loose[]).filter(
    (node) => !drop.has(String(node.id)),
  ) as unknown as Node[]

  edges.value = [
    ...(edges.value as unknown as { source: string; target: string }[]).filter(
      (edge) => !drop.has(edge.source) && !drop.has(edge.target),
    ),
    ...bridged
      /*
       * 上游/下游可能已经不存在了（比如被一起选中删掉），校验一遍再补。
       *
       * ⚠️⚠️ 这里的实参形态**曾经是错的**：`isStructurallyValid` 的契约是
       * **四个位置参数**（`source, sourceHandle, target, targetHandle`），
       * 而这里传了一个**对象**。于是除了第一个，其余全是 `undefined` ——
       * `findSocketIn` 找不到目标端口 → **永远返回 false** →
       * 补出来的边**一条都过不了**，表现就是「删掉接通节点，两边的线没了」
       * （用户报的正是这个）。
       *
       * ⚠️ 这个 bug 能活下来是因为**纯函数层测的是
       * `reconnectAroundReroutes`**（它是对的），而这一步的**接线**
       * 在 composable 里、没有套件覆盖。所以修复后在 `verify:reroute`
       * 里补了一条**形态断言**，把这个「四个参数」的契约钉住。
       */
      .filter((e) => isStructurallyValid(e.source, e.sourceHandle, e.target, e.targetHandle))
      .map((e) =>
        makeEdge(e.source, e.sourceHandle ?? '', e.target, e.targetHandle ?? ''),
      ),
  ] as unknown as Edge[]

  nextTick(() => flowStore.value?.updateNodeInternals())

  if (mode === 'frame-only' && plan.orphaned.length > 0) {
    /*
     * 「弹选项」但不打断流程：默认只删框，
     * 提示条里同时给「连子节点一起删」和「撤销」。
     */
    showNotice(`已删除框，${plan.orphaned.length} 个节点留在原地`, [
      {
        label: `连同 ${plan.orphaned.length} 个子节点一起删`,
        run: () => deleteNodesById(plan.orphaned),
      },
      { label: '撤销', run: doUndo },
    ])
    return
  }

  showNotice(`已删除 ${plan.deleted.length} 个节点`)
}

/** 按 id 直接删（提示条里的「连子节点一起删」用） */
function deleteNodesById(rawIds: readonly string[]) {
  const ids = rawIds.filter((id) => !isProtectedNode(id))
  if (ids.length === 0) return
  commit('删除节点')
  const drop = new Set(ids)
  type Loose = Record<string, unknown>

  nodes.value = (nodes.value as unknown as Loose[]).filter(
    (node) => !drop.has(String(node.id)),
  ) as unknown as Node[]
  edges.value = (
    edges.value as unknown as { source: string; target: string }[]
  ).filter((edge) => !drop.has(edge.source) && !drop.has(edge.target)) as unknown as Edge[]

  nextTick(() => flowStore.value?.updateNodeInternals())
  showNotice(`已删除 ${ids.length} 个节点`)
}

function selectAll() {
  flowStore.value?.addSelectedNodes?.(nodes.value as unknown as { id: string }[])
}

  return {
    onBoxResize,
    onRenameNode,
    onRepeaterAdd,
    onRenameRepeater,
    onRowToggle,
    onNoteUpdate,
    onEdgeDoubleClick,
    insertRerouteOnEdge,
    onPaneMenu,
    onSelectionMenu,
    onEdgeMenu,
    onGraphDoubleClick,
    deleteSelectedNodes,
    /*
     * ⚠️ 导出 `deleteNodesById` 是给**文档通道**用的
     * （`useEditorDoc` 的 `removeNodes`）：包编辑器要能删节点，而删除
     * 从来不只是"从数组里去掉"——级联边、受保护节点、框的成员都在这个
     * 函数里。让包自己拼一套 = 两套删除语义（迟早不一致）。
     */
    deleteNodesById,
    selectAll,
  }
}
